// Single-pin lifecycle: a state machine that makes cloning structurally
// impossible. Docked into a flow-layout lane (never floating in-plot), so a
// pin card can never occlude the plot, axis, or answer band.
//
// Dismissal has three equivalent paths: [x]; Esc (focus returns to the
// caller-supplied element); a document-level pointerdown listener attached
// at pin creation and detached at close, with inside/outside containment
// computed via event.composedPath() — shadow-DOM retargeting makes naive
// event.target checks misfire.
//
// Focus-steal fix: the outside-pointerdown path used to unconditionally
// call onDismissFocus (chartHost.focus()) on every dismiss, including when
// the "outside" press was actually a mousedown on a DIFFERENT header
// button (e.g. "validator income"/"explorer") a moment before its own click fires.
// Moving focus to another element mid-mousedown/pointerdown is a known
// browser quirk that can suppress the pending click on the original press
// target, which read as "header buttons don't register clicks" while a pin
// was open. The auto-dismiss path now skips the focus restore; only an
// explicit dismiss (Esc, the pin's own [x]) — where there is no competing
// click headed elsewhere — restores focus to the chart.

import { closeIcon } from "./icons";

export interface PinData {
  epoch: number;
  /** "Epoch 998 · Jul 8 to Jul 10". */
  header: string;
  /** `heading: true` rows render as a group label (e.g. "BASE" / "NET INCL MEV") instead of a label/value pair; `value` is ignored for those. */
  rows: { label: string; value: string; heading?: boolean }[];
  copyPayload: string;
  onDetails: () => void;
}

export interface PinLayerOptions {
  /** The plot host element: a press inside it never dismisses (the plot's own click handler resolves pin/move/focus/dismiss). */
  plotHost: HTMLElement;
  /** Called after every open/move/dismiss with the current pinned epoch (or null), so the caller can sync the in-plot amber tick. */
  onChange: (epoch: number | null) => void;
  /** Called on dismissal so the caller can return focus to the chart region. */
  onDismissFocus?: () => void;
}

const COPY_RESET_MS = 1200;
const COPY_FAIL_RESET_MS = 1600;

export class PinLayer {
  readonly #container: HTMLElement;
  readonly #plotHost: HTMLElement;
  readonly #onChange: (epoch: number | null) => void;
  readonly #onDismissFocus: (() => void) | undefined;

  #pinnedEpoch: number | null = null;
  #card: HTMLElement | null = null;
  #listenersActive = false;

  readonly #onDocPointerDown = (ev: PointerEvent): void => {
    const path = ev.composedPath();
    if (this.#card !== null && path.includes(this.#card)) {
      return;
    }
    if (path.includes(this.#plotHost)) {
      // Inside the plot: the plot's own click handler decides pin/move/focus/dismiss.
      return;
    }
    // Dismiss without stealing focus: the press is headed to some OTHER
    // element (a header button, band, etc.) that manages its own focus —
    // see the focus-steal fix note above.
    this.#dismissInternal(false);
  };

  readonly #onKeydown = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape") {
      this.dismiss();
    }
  };

  constructor(container: HTMLElement, opts: PinLayerOptions) {
    this.#container = container;
    this.#plotHost = opts.plotHost;
    this.#onChange = opts.onChange;
    this.#onDismissFocus = opts.onDismissFocus;
  }

  get pinnedEpoch(): number | null {
    return this.#pinnedEpoch;
  }

  /**
   * Opens a pin for `data.epoch`: pins if nothing is pinned, moves if a
   * different epoch is pinned, or focuses the existing card if the same
   * epoch is clicked again (dedupe keyed by epoch number).
   */
  open(data: PinData): void {
    if (this.#pinnedEpoch === data.epoch && this.#card !== null) {
      this.#focusCard();
      return;
    }
    this.#pinnedEpoch = data.epoch;
    this.#renderCard(data);
    this.#attachListeners();
    this.#onChange(this.#pinnedEpoch);
    this.#focusCard();
  }

  /** A plot click that resolved no epoch, Esc, or the pin's own [x]: dismiss and restore focus to the chart. */
  dismiss(): void {
    this.#dismissInternal(true);
  }

  #dismissInternal(restoreFocus: boolean): void {
    if (this.#pinnedEpoch === null) {
      return;
    }
    this.#pinnedEpoch = null;
    this.#card?.remove();
    this.#card = null;
    this.#detachListeners();
    this.#onChange(null);
    if (restoreFocus) {
      this.#onDismissFocus?.();
    }
  }

  /** Full teardown on disconnect — does not touch focus. */
  destroy(): void {
    this.#card?.remove();
    this.#card = null;
    this.#pinnedEpoch = null;
    this.#detachListeners();
  }

  #attachListeners(): void {
    if (this.#listenersActive) {
      return;
    }
    this.#listenersActive = true;
    document.addEventListener("pointerdown", this.#onDocPointerDown, true);
    document.addEventListener("keydown", this.#onKeydown, true);
  }

  #detachListeners(): void {
    if (!this.#listenersActive) {
      return;
    }
    this.#listenersActive = false;
    document.removeEventListener("pointerdown", this.#onDocPointerDown, true);
    document.removeEventListener("keydown", this.#onKeydown, true);
  }

  #focusCard(): void {
    this.#card?.focus({ preventScroll: true });
  }

  #renderCard(data: PinData): void {
    this.#container.replaceChildren();

    const card = document.createElement("div");
    card.className = "vf-pin-card";
    card.tabIndex = -1;

    const head = document.createElement("div");
    head.className = "vf-pin-head";
    const title = document.createElement("span");
    title.className = "vf-pin-title";
    title.textContent = data.header;

    const copy = document.createElement("button");
    copy.type = "button";
    copy.className = "vf-btn vf-pin-copy";
    copy.textContent = "copy";
    copy.setAttribute("aria-label", `copy ${data.header} figures`);
    // "copied"/"copy failed" are textContent swaps — announced only with
    // live-region semantics on the button itself.
    copy.setAttribute("aria-live", "polite");
    copy.addEventListener("click", () => {
      const failed = (): void => {
        copy.textContent = "copy failed";
        setTimeout(() => {
          copy.textContent = "copy";
        }, COPY_FAIL_RESET_MS);
      };
      // navigator.clipboard is undefined in non-secure embedding contexts;
      // unguarded, the click handler throws a synchronous TypeError instead
      // of reaching the visible failure state below.
      const clipboard = navigator.clipboard as Clipboard | undefined;
      if (clipboard === undefined) {
        failed();
        return;
      }
      clipboard.writeText(data.copyPayload).then(() => {
        copy.textContent = "copied";
        setTimeout(() => {
          copy.textContent = "copy";
        }, COPY_RESET_MS);
      }, failed);
    });

    // Close-affordance grammar: the same [x] .vf-btn every closable
    // surface uses (drill-down, embedded explorer), rightmost in
    // the head row — not the former bare 16px multiplication sign.
    const close = document.createElement("button");
    close.type = "button";
    close.className = "vf-btn vf-pin-close";
    close.classList.add("vf-close");
    close.append(closeIcon());
    close.setAttribute("aria-label", `close pin ${data.header}`);
    close.addEventListener("click", () => this.dismiss());

    head.append(title, copy, close);
    card.append(head);

    const body = document.createElement("div");
    body.className = "vf-pin-body";
    for (const row of data.rows) {
      if (row.heading === true) {
        const heading = document.createElement("div");
        heading.className = "vf-pin-group-label";
        heading.textContent = row.label;
        body.append(heading);
        continue;
      }
      const line = document.createElement("div");
      line.className = "vf-pin-row";
      const label = document.createElement("span");
      label.className = "vf-pin-label";
      label.textContent = row.label;
      const value = document.createElement("span");
      value.className = "vf-pin-value";
      value.textContent = row.value;
      line.append(label, value);
      body.append(line);
    }
    card.append(body);

    const details = document.createElement("button");
    details.type = "button";
    details.className = "vf-btn vf-pin-details";
    details.textContent = "details";
    details.addEventListener("click", data.onDetails);
    card.append(details);

    this.#container.append(card);
    this.#card = card;
  }
}
